import { z } from 'zod';
import { loginResponseSchema } from './auth.js';
/** The WEB apps with an origin that sign in through the central page. Mirrors Prisma SignInClient upper-cased
 *  (lock-step test, S50-kqp9-TASK-002). The partner portal is NOT one of them (it has its own
 *  handoff table and routes, S50-kqp9 Phase 1c; see `CENTRAL_SIGN_IN_CLIENTS`). Phase 5 adds Express as a
 *  HANDOFF client (`HANDOFF_SIGN_IN_CLIENTS`), not here: these each have an origin. */
/** S52-jecr: the platform super-admin app. A web client with an origin, so it IS in SIGN_IN_CLIENTS.
 *  Only a platform SUPERADMIN is ever issued a code for it (identity/service.ts#issueSignInHandoff, S52-jecr-TASK-008). */
export const CONTROL_TOWER_SIGN_IN_CLIENT = 'controltower';
export const SIGN_IN_CLIENTS = ['admin', 'visitor', 'directory', CONTROL_TOWER_SIGN_IN_CLIENT];
export const signInClientSchema = z.enum(SIGN_IN_CLIENTS);
/** The partner portal's client id on the central page (S50-kqp9 Phase 1c, ADR 0038 amendment).
 *  Deliberately NOT in SIGN_IN_CLIENTS: those are tenant clients, and no tenant schema may ever
 *  accept a partner. */
export const PARTNER_SIGN_IN_CLIENT = 'partner';
/** InstaCheckin Express (S50-kqp9 Phase 5, ADR 0038 Phase 5 amendment). A tenant client with NO origin:
 *  its code goes to a fixed custom-scheme URL. Deliberately NOT in SIGN_IN_CLIENTS (each of those has an origin). */
export const EXPRESS_SIGN_IN_CLIENT = 'express';
/** Every client the central page serves (`?client=`): the web tenant apps, then the partner portal, then InstaCheckin Express. */
export const CENTRAL_SIGN_IN_CLIENTS = [
    ...SIGN_IN_CLIENTS,
    PARTNER_SIGN_IN_CLIENT,
    EXPRESS_SIGN_IN_CLIENT,
];
export const centralSignInClientSchema = z.enum(CENTRAL_SIGN_IN_CLIENTS);
/** Every client a TENANT handoff code can be issued to and redeemed by. Mirrors Prisma SignInClient upper-cased. */
export const HANDOFF_SIGN_IN_CLIENTS = [...SIGN_IN_CLIENTS, EXPRESS_SIGN_IN_CLIENT];
export const handoffSignInClientSchema = z.enum(HANDOFF_SIGN_IN_CLIENTS);
/** Express's registered scheme = its applicationId / bundle id. Not a secret; PKCE is the proof. */
export const EXPRESS_SIGN_IN_CALLBACK_SCHEME = 'io.instanexus.express';
/** The only place an Express handoff code is ever sent. The same in every environment. */
export const EXPRESS_SIGN_IN_CALLBACK_URL = `${EXPRESS_SIGN_IN_CALLBACK_SCHEME}://auth/callback`;
/** A handoff code is good for this long, once. */
export const SIGN_IN_HANDOFF_TTL_SECONDS = 60;
/** Every app's handoff landing route (same path on every origin). */
export const SIGN_IN_CALLBACK_PATH = '/auth/callback';
/** The central app's routes. */
export const CENTRAL_SIGN_IN_PATH = '/login';
export const CENTRAL_SIGN_OUT_PATH = '/logout';
/** Query-parameter names, shared so the two sides can never drift. `next` FIRST in every URL built
 *  (e2e asserts `/login?next=`). */
export const SIGN_IN_PARAMS = {
    next: 'next',
    client: 'client',
    codeChallenge: 'code_challenge',
    state: 'state',
    code: 'code',
};
export const SIGN_IN_RETURN_PATH_MAX = 512;
/** Same-origin RELATIVE path: one leading '/', never '//' or '/\', no backslash or whitespace. */
export const signInReturnPathSchema = z
    .string()
    .min(1)
    .max(SIGN_IN_RETURN_PATH_MAX)
    .regex(/^\/(?![/\\])[^\s\\]*$/);
/** base64url(SHA-256(verifier)) — RFC 7636 S256, always 43 chars. */
export const pkceChallengeSchema = z.string().regex(/^[A-Za-z0-9_-]{43}$/);
/** RFC 7636 verifier: 43–128 unreserved chars. */
export const pkceVerifierSchema = z.string().regex(/^[A-Za-z0-9._~-]{43,128}$/);
/** The app's CSRF state, echoed back untouched. */
export const signInStateSchema = z.string().regex(/^[A-Za-z0-9_-]{16,128}$/);
/** 32 random bytes, base64url — 43 chars. */
export const signInHandoffCodeSchema = z.string().regex(/^[A-Za-z0-9_-]{43}$/);
/** `POST /api/web/auth/handoff` — on the CENTRAL origin; the central session cookie is the credential. */
export const issueSignInHandoffSchema = z
    .object({
    // web clients and Express (ADR 0038 Phase 5)
    client: handoffSignInClientSchema,
    codeChallenge: pkceChallengeSchema,
    state: signInStateSchema,
    returnTo: signInReturnPathSchema,
})
    .strict();
export const signInHandoffDTOSchema = z.object({ redirectUrl: z.string().url() }).strict();
/** `POST /api/web/auth/handoff/exchange` — on the APP's origin. */
export const exchangeSignInHandoffSchema = z
    .object({
    // web clients and Express (ADR 0038 Phase 5)
    client: handoffSignInClientSchema,
    code: signInHandoffCodeSchema,
    codeVerifier: pkceVerifierSchema,
})
    .strict();
export const signInHandoffExchangeDTOSchema = loginResponseSchema
    .extend({ returnTo: signInReturnPathSchema })
    .strict();
/** `POST /api/web/partner/auth/handoff` — on the CENTRAL origin; the central PARTNER session cookie
 *  (`inx_partner_session`) is the credential. Response: `signInHandoffDTOSchema`. */
export const issuePartnerSignInHandoffSchema = z
    .object({
    client: z.literal(PARTNER_SIGN_IN_CLIENT),
    codeChallenge: pkceChallengeSchema,
    state: signInStateSchema,
    returnTo: signInReturnPathSchema,
})
    .strict();
/** `POST /api/web/partner/auth/handoff/exchange` — on the PARTNER PORTAL's origin.
 *  Response: `signInHandoffExchangeDTOSchema` (token + returnTo), the same as the tenant exchange. */
export const exchangePartnerSignInHandoffSchema = z
    .object({
    client: z.literal(PARTNER_SIGN_IN_CLIENT),
    code: signInHandoffCodeSchema,
    codeVerifier: pkceVerifierSchema,
})
    .strict();
/** The central app's public auth pages (ADR 0038). Apps redirect their old URLs here. */
export const CENTRAL_AUTH_PAGE_PATHS = {
    /** Ask for a password reset code. */
    forgotPassword: '/forgot-password',
    /** Redeem a reset code and choose a new password. */
    resetPassword: '/reset-password',
    /** Create an account. */
    signup: '/signup',
    /** Confirm an email address with a code. */
    verifyEmail: '/verify-email',
    /** Confirm a phone number with a code. */
    verifyPhone: '/verify-phone',
    /** Accept an invitation to a tenant. */
    acceptInvite: '/accept-invite',
    /** The Terms interstitial. */
    terms: '/terms',
};
//# sourceMappingURL=sign-in-handoff.js.map